import {
  CONTRACT,
  ContractEventParams,
  useApproveErc721,
  useContract
} from '@apeiron/contract'
import { CurrencyTokenTypeERC20, useCurrencyContext } from '@apeiron/currency'
import {
  CustomError,
  ERROR_CODE,
  useAccountContext,
  useChainSwitcher,
  useMessageContext
} from '@apeiron/temp'
import { ApeironPleaseWait } from '@apeiron/ui'
import { useMktErrorHandling } from '@apeiron/wallet'
import useAllowCurrency from '@src/hooks/contract/useAllowCurrency'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Address } from 'viem'

const approvalError = new CustomError(
  'user reject to approve token for operation',
  ERROR_CODE.CONTRACT.APPROVAL_FAIL
)

const useBounty = () => {
  const { sendWithOption } = useContract()

  const { requestCurrency } = useAllowCurrency()

  const { setInfo } = useMessageContext()

  const { getCurrencyAddress } = useCurrencyContext()

  const { showErrorPopup } = useMktErrorHandling()

  const { getContractAddress } = useChainSwitcher()

  const { account } = useAccountContext()

  const approveApostle = useApproveErc721(
    account.walletAddress as Address,
    getContractAddress(CONTRACT.APOSTLE.GET_USER_ALLOWANCE.address)
  )

  const { t } = useTranslation()

  const createBounty = useCallback(async (params: CreateBountyParams) => {
    try {
      const contract = CONTRACT.BOUNTY.APOSTLE.CREATE_BOUNTY

      const allowed = await requestCurrency(
        account.walletAddress as Address,
        getContractAddress(contract.address),
        params.currency,
        BigInt(params.pricePerApostle) * BigInt(params.numberToBuy),
        {
          checkMessage: t('common.popup.transaction.check_approval'),
          waitMessage: t('common.wait_token_approve')
        }
      )

      if (!allowed) {
        throw new CustomError(
          'User not allow to spend weth',
          ERROR_CODE.CONTRACT.APPROVAL_FAIL
        )
      }

      const {
        classId,
        minIV,
        mainSkillId,
        otherSkillId1,
        otherSkillId2,
        numberToBuy,
        pricePerApostle,
        minMainSkillLevel,
        minOtherSkill1Level,
        minOtherSkill2Level
      } = params

      setInfo({
        message: <ApeironPleaseWait subject={t('bounty.creating')} />
      })

      await sendWithOption(
        {
          contract,
          verifyEvent: ContractEventParams.CreateBounty
        },
        classId,
        mainSkillId,
        otherSkillId1,
        otherSkillId2,
        [minMainSkillLevel, minOtherSkill1Level, minOtherSkill2Level],
        minIV,
        numberToBuy,
        getCurrencyAddress(params.currency),
        pricePerApostle
      )

      setInfo(null)

      return true
    } catch (error) {
      showErrorPopup(error)

      return false
    }
  }, [])

  const claimBounty = useCallback(async (params: ClaimBountyParams) => {
    try {
      const bountyContract = CONTRACT.BOUNTY.APOSTLE.FULFILL_BOUNTY

      const bountyAddress = getContractAddress(bountyContract.address)

      setInfo({
        title: t('dialog.claiming_bounty.title'),
        message: t('common.popup.transaction.be_patient')
      })

      const approved = await approveApostle(bountyAddress)

      if (!approved) {
        throw approvalError
      }

      const { bountyId, apostleIds } = params

      await sendWithOption(
        {
          contract: bountyContract,
          verifyEvent: ContractEventParams.FulfillBounty
        },
        bountyId,
        apostleIds
      )

      setInfo(null)

      return true
    } catch (error) {
      showErrorPopup(error)

      return false
    }
  }, [])

  const cancelBounty = useCallback(async (params: CancelBountyParams) => {
    console.info('cancelBounty', params)
    try {
      const bountyContract = CONTRACT.BOUNTY.APOSTLE.CANCEL_BOUNTY

      setInfo({
        title: t('dialog.cancel_bounty.title'),
        message: t('common.popup.transaction.be_patient')
      })

      const { bountyId } = params

      await sendWithOption(
        {
          contract: bountyContract,
          verifyEvent: ContractEventParams.CancelBounty
        },
        bountyId
      )

      setInfo(null)

      return true
    } catch (error) {
      showErrorPopup(error)

      return false
    }
  }, [])

  return {
    createBounty,
    claimBounty,
    cancelBounty
  }
}

type CreateBountyParams = {
  classId: number
  minIV: number
  mainSkillId: string
  otherSkillId1: string
  otherSkillId2: string
  numberToBuy: number
  pricePerApostle: string
  minMainSkillLevel: number
  minOtherSkill1Level: number
  minOtherSkill2Level: number
  currency: CurrencyTokenTypeERC20
}

type ClaimBountyParams = {
  bountyId: string
  apostleIds: string[]
}

type CancelBountyParams = {
  bountyId: number
}

export default useBounty
